import { useEffect, useRef } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { gaPageView, initAnalytics } from './lib/analytics';
import { fbqTrack } from './lib/metaPixel';
import { CookieBanner } from './components/CookieBanner';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { WhatsAppFab } from './components/WhatsAppFab';
import { About } from './pages/About';
import { Calendrier } from './pages/Calendrier';
import { Catalogue } from './pages/Catalogue';
import { EvenementDetail } from './pages/EvenementDetail';
import { EvenementInscription } from './pages/EvenementInscription';
import { EvenementsList } from './pages/EvenementsList';
import { FormateurDetail } from './pages/FormateurDetail';
import { FormateursList } from './pages/FormateursList';
import { FormationDetail } from './pages/FormationDetail';
import { Galerie } from './pages/Galerie';
import { Home } from './pages/Home';
import { Inscription } from './pages/Inscription';
import { Login } from './pages/Login';
import { Membre } from './pages/Membre';
import { MonEspace } from './pages/MonEspace';
import { NotFound } from './pages/NotFound';
import { PartenaireDetail } from './pages/PartenaireDetail';
import { Privacy } from './pages/Privacy';
import { Publications } from './pages/Publications';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

/**
 * GA4 page-view tracker. Tries to init on mount (works if consent was given in
 * a previous visit) and sends a page_view on every route change. Both calls
 * are no-ops until consent is granted, so this is safe to always render.
 */
function AnalyticsTracker() {
  const { pathname, search } = useLocation();
  const firstPixelView = useRef(true);
  useEffect(() => {
    initAnalytics();
  }, []);
  useEffect(() => {
    gaPageView(pathname + search);
    // Meta Pixel: the base snippet in index.html already fired the initial
    // PageView on document load, so only fire on subsequent SPA navigations
    // (same 'PageView' event — unchanged).
    if (firstPixelView.current) {
      firstPixelView.current = false;
    } else {
      fbqTrack('PageView');
    }
  }, [pathname, search]);
  return null;
}

function Layout() {
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  return (
    <div className={`mtc-app ${isHome ? 'home-active home-modern' : ''}`} data-home-variant="modern">
      <Header />
      <main className="mtc-main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/formations" element={<Catalogue />} />
          <Route path="/formations/:id" element={<FormationDetail />} />
          <Route path="/calendrier" element={<Calendrier />} />
          <Route path="/formateurs" element={<FormateursList />} />
          <Route path="/formateurs/:id" element={<FormateurDetail />} />
          <Route path="/evenements" element={<EvenementsList />} />
          <Route path="/evenements/:id" element={<EvenementDetail />} />
          <Route path="/evenements/:id/inscription" element={<EvenementInscription />} />
          <Route path="/galerie" element={<Galerie />} />
          <Route path="/partenaires/:id" element={<PartenaireDetail />} />
          <Route path="/inscription" element={<Inscription />} />
          {/* Dedicated thank-you landing (ad conversion tracking). Renders the
              same component; a direct visit without a completed submission
              redirects back to the form. */}
          <Route path="/inscription/merci" element={<Inscription />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/connexion" element={<Login />} />
          <Route path="/mon-espace" element={<MonEspace />} />
          <Route path="/publications" element={<Publications />} />
          <Route path="/membre" element={<Membre />} />
          <Route path="/confidentialite" element={<Privacy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppFab />
      <CookieBanner />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AnalyticsTracker />
      <Layout />
    </BrowserRouter>
  );
}
